import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { 
    Paper, 
    Typography, 
    Fade, 
    Button, 
    Box, 
    Divider,
    Container,
    Tooltip
} from '@mui/material';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import DownloadIcon from '@mui/icons-material/Download';
import { useWebSocket } from './WebSocketContext'; // Adjust the import path as necessary
import { apiRequest } from '../Utils/apiClient';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';

// Memoized style constants
const STYLES = {
    paper: {
        borderRadius: 2,
        background: 'white',
        padding: 2,
        boxSizing: 'border-box',
        maxWidth: '100%',
        width: '100%',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
            boxShadow: '0 8px 24px rgba(87, 87, 176, 0.12)'
        }
    },
    controlButton: {
        backgroundColor: 'rgba(87, 87, 176, 0.1)',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(87, 87, 176, 0.2)',
            transform: 'translateY(-2px)'
        }
    }
};

const SoftwareCanBeUpdated = () => {
    const { sendMessage, storageInfo } = useWebSocket();
    const [show, setShow] = useState(false);
    const { t } = useTranslation();
    const isFolderRestricted = Boolean(storageInfo?.restrictToUserFolder);
    
    // Theme colors - matching Dashboard
    const primaryColor = '#5757b0';
    const hoverColor = '#7777d0';
    const selectedColor = '#4545a0';
    const lightPurple = '#f0f0ff';

    const getSoftwareUpdateStatus = useCallback(async () => {
        try {
            const payload = await apiRequest('/api/v1/software/update-status');
            setShow(payload.status === 'yes' || payload.can_be_updated === true);
        } catch (error) {
            console.error('Failed to fetch software update status:', error);
            setShow(false);
        }
    }, []);

    useEffect(() => {
        getSoftwareUpdateStatus();
    }, [getSoftwareUpdateStatus]);

    const handleClose = () => {
        setShow(false);
    };

    const handleDownload = () => {
        if (sendMessage) {
            sendMessage({ command: 'downloadSoftwareUpdate' });
        }
    };

    const downloadButton = useMemo(() => (
        <Tooltip title={t('software_update.download_tooltip')} arrow>
            <Button
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                size="small"
                variant="contained"
                sx={{
                    mr: 1,
                    backgroundColor: primaryColor,
                    color: 'white',
                    boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
                    '&:hover': {
                        backgroundColor: hoverColor,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(87, 87, 176, 0.35)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    textTransform: 'none',
                    fontWeight: 'medium',
                    borderRadius: '8px',
                    padding: '6px 16px',
                }}
            >
                {t('software_update.download_button')}
            </Button>
        </Tooltip>
    ), [handleDownload, primaryColor, hoverColor, t]);

    if (!show) return null;    
    return (
        <Fade in={show} timeout={800}>
            <Box sx={{ 
                position: 'fixed',
                top: 15,
                left: 0,
                right: 0,
                zIndex: 9999,
                margin: '0 auto',
                pointerEvents: 'none',
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
            }}>
                <Container maxWidth="md" sx={{ 
                    px: { xs: 2, sm: 3 },
                    pointerEvents: 'none'
                }}>
                    <Paper
                        elevation={3}
                        sx={{
                            ...STYLES.paper,
                            pointerEvents: 'auto',
                            position: 'relative',
                            overflow: 'visible',
                            backdropFilter: 'blur(10px)',
                            backgroundColor: 'white',
                            boxShadow: '0 4px 15px rgba(87, 87, 176, 0.2)',
                        }}
                    >
                        <Box sx={{ position: 'relative' }}>
                            {/* Header section with icon and title */}
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                mb: 0.5
                            }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    flex: 1
                                }}>
                                    <SystemUpdateIcon sx={{ 
                                        mr: 1.5, 
                                        color: primaryColor,
                                        fontSize: '1.5rem',
                                        animation: 'pulse 2s infinite',
                                        '@keyframes pulse': {
                                            '0%': { opacity: 0.7 },
                                            '50%': { opacity: 1 },
                                            '100%': { opacity: 0.7 },
                                        }
                                    }} />
                                    <Typography 
                                        variant="h6" 
                                        sx={{
                                            fontWeight: 600,
                                            color: '#2c3e50',
                                            textAlign: 'center',
                                            flex: 1
                                        }}
                                    >
                                        {t('software_update.title')}
                                    </Typography>
                                </Box>
                                
                                <CloseButton 
                                    size="small" 
                                    onClick={handleClose} 
                                    ariaLabel="close"
                                    sx={{ 
                                        color: 'rgba(0, 0, 0, 0.5)',
                                        '&:hover': { 
                                            color: 'rgba(0, 0, 0, 0.8)',
                                            backgroundColor: 'rgba(87, 87, 176, 0.08)'
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                />
                            </Box>
                            
                            <Divider sx={{ 
                                width: '100%', 
                                my: 1, 
                                borderColor: 'rgba(87, 87, 176, 0.2)',
                                opacity: 0.7
                            }} />
                            
                            {/* Description and download button */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                px: 1,
                                py: 1
                            }}>
                                <Typography variant="body2" sx={{ 
                                    color: '#5a6a7a',
                                    maxWidth: '70%'
                                }}>
                                    {t('software_update.description')}
                                    {!isFolderRestricted && (
                                        <Box component="span" sx={{ 
                                            display: 'block', 
                                            mt: 0.5, 
                                            fontSize: '0.8rem',
                                            fontStyle: 'italic',
                                            opacity: 0.8
                                        }}>
                                            {t('software_update.new_version_available')}
                                        </Box>
                                    )}
                                </Typography>
                                
                                {!isFolderRestricted && downloadButton}
                            </Box>
                        </Box>
                    </Paper>
                </Container>
            </Box>
        </Fade>
    );
};

export default SoftwareCanBeUpdated;
